import React, { createContext, useContext, useRef, useCallback, useEffect } from 'react';

/**
 * Tracks unsaved edits across tabs so they can't be silently discarded.
 *
 * Several screens hold edits in local state until an explicit Save (the
 * Accounts tab's "Save *", Strategy's "Save all"). Switching tab or month
 * threw that work away with no warning, which reads as "I changed it and
 * nothing happened". Tabs register their dirty state here; App.js asks
 * confirmDiscard() before navigating, and the browser is guarded too.
 *
 * Dirty flags live in a ref, not state — they're read at navigation time and
 * should never trigger a re-render of the whole app on every keystroke.
 */
const UnsavedContext = createContext({
  setDirty: () => {},
  isDirty: () => false,
  confirmDiscard: () => true,
});

export function UnsavedChangesProvider({ children }) {
  const dirtyRef = useRef(new Map());

  const setDirty = useCallback((key, dirty) => {
    if (dirty) dirtyRef.current.set(key, true);
    else dirtyRef.current.delete(key);
  }, []);

  const isDirty = useCallback(() => dirtyRef.current.size > 0, []);

  const confirmDiscard = useCallback(() => {
    if (dirtyRef.current.size === 0) return true;
    const where = [...dirtyRef.current.keys()].join(', ');
    return window.confirm(
      `You have unsaved changes in ${where}.\n\n` +
      'Leaving now discards them. Continue anyway?'
    );
  }, []);

  // Closing/reloading the tab gets the browser's own native warning.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (dirtyRef.current.size === 0) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  return (
    <UnsavedContext.Provider value={{ setDirty, isDirty, confirmDiscard }}>
      {children}
    </UnsavedContext.Provider>
  );
}

export const useUnsavedChanges = () => useContext(UnsavedContext);

/**
 * Registers one screen's dirty state. `label` is what the user sees in the
 * warning, so name it after the tab ("Accounts", "Strategy").
 */
export function useUnsavedFlag(label, dirty) {
  const { setDirty } = useUnsavedChanges();
  useEffect(() => {
    setDirty(label, dirty);
    return () => setDirty(label, false); // unmounting means the edits are gone anyway
  }, [label, dirty, setDirty]);
}
